<template>
    <div
        class="moreMarketDisplay"
        v-bind:class="{ _rotate_table: isAsia1x2(oddsList[0]) }"
    >
        <div
            class="moreMarketDisplay_type"
            v-bind:class="{ _isMarketShow: isMarketShow }"
            v-on:click="isMarketShow = !isMarketShow"
        >
            <span>
                <span v-if="!isMainMarket">{{ marketGroupName }} -
                </span>
                {{
                    getMarketDisplayTypeName(
                        oddsList[0].marketType,
                        oddsList[0].marketDisplayTypeName,
                    )
                }}
                <span v-if="scoreOrCorner" class="scoreOrCorner">{{
                    scoreOrCorner
                }}</span>
                <ScorePredictionPromotionBadge
                    v-if="isPromotionBadgeShown"
                    gaPage="MatchCard"
                    gaLabel="CS"
                />
            </span>
            <SvgIcon iconName="systemIcon_arrowBottom" />
        </div>
        <TransitionCollapse>
            <div v-if="isMarketShow" class="moreMarketDisplay_columnWrapper">
                <template v-for="oddsWithGrouping in oddsListWithGrouping" v-bind:key="oddsWithGrouping.odds.oddsId">
                    <div
                        v-if="oddsWithGrouping.groupKeys != null"
                        v-bind:key="`title-${oddsWithGrouping.odds.oddsId}`"
                        class="moreMarketDisplay_columnName"
                    >
                        <div
                            v-for="key in oddsWithGrouping.groupKeys"
                            v-bind:key="key"
                        >
                            {{ key }}
                        </div>
                    </div>
                    <div
                        class="moreMarketDisplay_content"
                        v-bind:class="{ 'two-per-row': isTwoPerRow(oddsWithGrouping.odds) }"
                    >
                        <div
                            v-for="(group, key) in (oddsWithGrouping.groupedPrices)"
                            v-bind:key="key"
                            class="moreMarketDisplay_oddGroup"
                        >
                            <template v-for="price in group">
                                <Odds
                                    v-if="isAsia1x2(oddsWithGrouping.odds)"
                                    v-bind:key="
                                        `asia1x2-${oddsWithGrouping.odds.oddsId}:${price.option}`
                                    "
                                    v-bind:oddsInfo="oddsWithGrouping.odds"
                                    v-bind:priceOption="price"
                                    v-bind:oddsPoint="oddsWithGrouping.odds.point"
                                    v-bind:matchInfo="match"
                                    v-bind:marketOrder="marketOrder"
                                    v-bind:displaySection="displaySection"
                                    class="moreMarketDisplay_odd"
                                />
                                <OptionOdds
                                    v-else
                                    v-bind:key="
                                        `${oddsWithGrouping.odds.oddsId}:${price.option}`
                                    "
                                    v-bind:oddsInfo="oddsWithGrouping.odds"
                                    v-bind:priceOption="price"
                                    v-bind:oddsPoint="oddsWithGrouping.odds.point"
                                    v-bind:matchInfo="match"
                                    v-bind:marketOrder="marketOrder"
                                    v-bind:displaySection="displaySection"
                                    class="moreMarketDisplay_odd"
                                />
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </TransitionCollapse>
    </div>
</template>

<script lang="ts">
    import type { PropType } from 'vue';
    import {
        computed,
        defineComponent,
        onMounted,
        ref,
        watch,
    } from 'vue';
    import TransitionCollapse from '@/components/common/TransitionCollapse.vue';
    import Odds from '@/components/match/Odds.vue';
    import OptionOdds from '@/components/match/OptionOdds.vue';
    import ScorePredictionPromotionBadge from '@/components/promotion/ScorePredictionPromotionBadge.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds } from '@/interface/IOdds';
    import { DisplaySection, MarketType, SportType } from '@/interface/enum';
    import { useScorePredictionPromotion } from '@/libraries/composables/useScorePredictionPromotion';
    import { groupAsia1X2, groupCorrectScore, groupFgLg, groupHtft } from '@/libraries/helpers/moreMarketGroupHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { getScore } from '@/libraries/marketDisplay';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MoreMarket',
        components: {
            Odds,
            OptionOdds,
            TransitionCollapse,
            SvgIcon,
            ScorePredictionPromotionBadge,
        },
        props: {
            oddsList: {
                type: Array as PropType<IOdds[]>,
                default: () => [],
            },
            match: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
            marketOrder: {
                type: Number,
                default: () => -1,
            },
            isCollapse: {
                type: Boolean,
                default: () => false,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props) {
            const { isPromotionMarket } = useScorePredictionPromotion();
            const isPromotionBadgeShown = computed(() => isPromotionMarket(props.match.leagueName, props.oddsList[0]));

            const marketGroupName = computed(() => getTranslated(props.oddsList[0].marketGroup.name));

            function getMarketDisplayTypeName(marketType: MarketType, marketDisplayTypeKey: string) {
                const baseballFhMapping: Partial<Record<MarketType, string>> = {
                    [MarketType.FH_Handicap]: 'marketType_Handicap_Name',
                    [MarketType.FH_OverUnder]: 'marketType_OverUnder_Name',
                    [MarketType.FH_OddEven]: 'marketType_OddEven_Name',
                    [MarketType.FH_1X2]: 'marketType__1X2_Name',
                    [MarketType.FH_CorrectScore]: 'marketType_CorrectScore_Name',
                    [MarketType.FH_LiveScore]: 'marketType_LiveScore_Name',
                };

                if (
                    props.match.sportType === SportType.Baseball
                    && baseballFhMapping[marketType]
                ) {
                    return `${i18n.t(
                        'oddsDisplay_matchInfo_firstHalf_baseball',
                    )} ${i18n.t(baseballFhMapping[marketType])}`;
                }
                return i18n.t(marketDisplayTypeKey);
            }

            const pricesGrouping = (odds: IOdds) => {
                switch (odds.marketType) {
                    case MarketType.CorrectScore:
                    case MarketType.FH_CorrectScore:
                    case MarketType.SH_CorrectScore:
                        return groupCorrectScore(odds, props.match.mainMarketEventResult.liveHomeScore, props.match.mainMarketEventResult.liveAwayScore);
                    case MarketType.FGLG:
                        return groupFgLg(odds);
                    case MarketType.HTFT:
                        return groupHtft(odds);
                    case MarketType.LiveScore:
                    case MarketType.FH_LiveScore:
                        return groupAsia1X2(odds, props.match.homeTeam.teamName, props.match.awayTeam.teamName);
                    default:
                        return odds.priceOption.map(p => [p]);
                }
            };

            const isMainMarket = computed(
                () => props.oddsList[0].marketGroup.id === 0,
            );

            const isAsia1x2 = (odds: IOdds) => [MarketType.LiveScore, MarketType.FH_LiveScore].includes(
                odds.marketType,
            );

            const isTwoPerRow = (odds: IOdds) => odds.marketType === MarketType.TotalGoal;

            const scoreOrCorner = computed(() => getScore(props.match.isLive, props.oddsList[0], props.match.sportType));

            const oddsListWithGrouping = computed(() => props.oddsList.map((odds) => {
                const result = pricesGrouping(odds);
                return {
                    odds,
                    groupedPrices: Array.isArray(result) ? result : Object.values(result),
                    groupKeys: Array.isArray(result) ? null : Object.keys(result),
                };
            }));

            const isMarketShow = ref(false);

            onMounted(() => {
                isMarketShow.value = props.marketOrder === 1;
            });

            watch(
                () => props.isCollapse,
                (newVal) => {
                    isMarketShow.value = newVal;
                },
            );

            return {
                isMainMarket,
                isAsia1x2,
                isTwoPerRow,
                isMarketShow,
                scoreOrCorner,
                oddsListWithGrouping,
                getMarketDisplayTypeName,
                marketGroupName,
                isPromotionBadgeShown,
            };
        },
    });
</script>

<style lang="scss" scoped>
.moreMarketDisplay {
    background: $moreMarketDisplay_bg;

    &_type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.7rem 1rem;
        margin-bottom: 0.1rem;
        background: $moreMarketDisplay_type_bg;
        color: #4b4b4b;
        font-weight: bold;

        .svgIcon {
            width: 1.2em;
            height: 1.2em;
            fill: rgba($moreMarketDisplay_type_color, 0.7);
            transition: transform 0.3s;
        }

        &._isMarketShow .svgIcon {
            transform: rotate(-180deg);
        }
    }

    &:last-child > &_type {
        margin-bottom: 0;
    }

    &_content {
        display: flex;
        flex: 1 1;
        flex-direction: row;
        padding: 0 0.5em 0 1em;
    }

    &_content:last-child {
        padding-bottom: 1em;
    }

    &_content.two-per-row {
        flex-wrap: wrap;

        .moreMarketDisplay_oddGroup {
            max-width: 50%;
            flex: 0 0 50%;
        }
    }

    &_oddGroup {
        flex: 1;
        text-align: center;

        &:last-child {
            border: none;
        }
    }

    &_columnWrapper {
        display: flex;
        flex-direction: column;
    }

    &_columnName {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        padding: 0.3em;
        color: $moreMarketDisplay_columnName_color;
        text-align: center;
    }

    &_odd {
        flex-direction: row;
        align-items: center;
        margin: 0.5em 0.5em 0 0;
    }
}

.__live {
    &.moreMarketDisplay {
        background: $moreMarketDisplay_bg_live;

        .moreMarketDisplay_type {
            background: $moreMarketDisplay_type_bg_live;

            .scoreOrCorner {
                color: #ff7777;
            }
        }
    }
}

._rotate_table {
    &.moreMarketDisplay {
        .moreMarketDisplay_columnWrapper {
            flex-direction: row;
        }

        .moreMarketDisplay_columnName {
            flex-direction: column;
            padding-left: 1rem;
            text-align: left;
        }

        .moreMarketDisplay_content {
            flex-direction: column;
        }
    }
}
</style>
